import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Users, BookOpen, MapPin, MessageSquare, LogOut, Shield, Globe,
  Star, FileText, Settings, Compass, BarChart3, Image,
} from "lucide-react";
import { fetchAdmin } from "../utils/adminApi";
import ToursPanel from "../components/admin/ToursPanel";
import BlogPanel from "../components/admin/BlogPanel";
import TestimonialsPanel from "../components/admin/TestimonialsPanel";
import ProgramsPanel from "../components/admin/ProgramsPanel";
import BookingsPanel from "../components/admin/BookingsPanel";
import UsersPanel from "../components/admin/UsersPanel";
import SiteSettingsPanel from "../components/admin/SiteSettingsPanel";
import AdventureBuilderPanel from "../components/admin/AdventureBuilderPanel";
import AnalyticsPanel from "../components/admin/AnalyticsPanel";
import MediaPanel from "../components/admin/MediaPanel";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [tours, setTours] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [sRes, uRes, bRes, pRes, tRes, blRes, teRes] = await Promise.all([
        fetchAdmin("/api/admin/stats"), fetchAdmin("/api/admin/users"),
        fetchAdmin("/api/admin/bookings"), fetchAdmin("/api/admin/programs"),
        fetchAdmin("/api/content/admin/tours"), fetchAdmin("/api/content/admin/blog"),
        fetchAdmin("/api/content/admin/testimonials"),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (uRes.ok) setUsers(await uRes.json());
      if (bRes.ok) setBookings(await bRes.json());
      if (pRes.ok) setPrograms(await pRes.json());
      if (tRes.ok) setTours(await tRes.json());
      if (blRes.ok) setBlogPosts(await blRes.json());
      if (teRes.ok) setTestimonials(await teRes.json());
    } catch (err) { console.error("Admin load failed:", err); }
    finally { setLoading(false); }
  }

  if (loading) return <div className="min-h-screen bg-stone-950 pt-20 flex justify-center items-center"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500" /></div>;

  return (
    <div className="min-h-screen bg-stone-950 pt-20 pb-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2"><Shield className="w-6 h-6 text-amber-500" /><h1 className="text-3xl font-bold text-white" data-testid="admin-heading">Admin Dashboard</h1></div>
            <p className="text-stone-400 mt-1">{user?.name} - {user?.email}</p>
          </div>
          <Button onClick={logout} variant="outline" className="border-stone-700 text-stone-300 hover:bg-stone-800 rounded-xl self-start" data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-2" />Sign Out</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Users", value: stats.total_users || 0, icon: Users, color: "text-blue-400" },
            { label: "Programs", value: stats.total_programs || 0, icon: BookOpen, color: "text-emerald-400" },
            { label: "Bookings", value: stats.total_bookings || 0, icon: MapPin, color: "text-amber-400" },
            { label: "Contacts", value: stats.total_contacts || 0, icon: MessageSquare, color: "text-purple-400" },
          ].map(s => (
            <div key={s.label} className="bg-stone-900/60 border border-stone-800 rounded-xl p-4" data-testid={`admin-stat-${s.label.toLowerCase()}`}>
              <s.icon className={`w-5 h-5 ${s.color} mb-2`} /><p className="text-2xl font-bold text-white">{s.value}</p><p className="text-stone-400 text-sm">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="tours">
          <TabsList className="bg-stone-900/80 border border-stone-800 rounded-xl p-1 mb-6 flex flex-wrap gap-1">
            <TabsTrigger value="tours" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-tours"><Globe className="w-4 h-4 mr-1" />Tours</TabsTrigger>
            <TabsTrigger value="blog" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-blog"><FileText className="w-4 h-4 mr-1" />Blog</TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-testimonials"><Star className="w-4 h-4 mr-1" />Reviews</TabsTrigger>
            <TabsTrigger value="programs" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-programs"><BookOpen className="w-4 h-4 mr-1" />Programs</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-bookings"><MapPin className="w-4 h-4 mr-1" />Bookings</TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1" />Users</TabsTrigger>
            <TabsTrigger value="builder" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-builder"><Compass className="w-4 h-4 mr-1" />Builder</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-settings"><Settings className="w-4 h-4 mr-1" />Site</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-analytics"><BarChart3 className="w-4 h-4 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="media" className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400" data-testid="admin-tab-media"><Image className="w-4 h-4 mr-1" />Media</TabsTrigger>
          </TabsList>

          <TabsContent value="tours"><ToursPanel tours={tours} onRefresh={loadAll} /></TabsContent>
          <TabsContent value="blog"><BlogPanel blogPosts={blogPosts} onRefresh={loadAll} /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsPanel testimonials={testimonials} onRefresh={loadAll} /></TabsContent>
          <TabsContent value="programs"><ProgramsPanel programs={programs} onRefresh={loadAll} /></TabsContent>
          <TabsContent value="bookings"><BookingsPanel bookings={bookings} users={users} programs={programs} onRefresh={loadAll} /></TabsContent>
          <TabsContent value="users"><UsersPanel users={users} programs={programs} onRefresh={loadAll} /></TabsContent>
          <TabsContent value="builder"><AdventureBuilderPanel /></TabsContent>
          <TabsContent value="settings"><SiteSettingsPanel /></TabsContent>
          <TabsContent value="analytics"><AnalyticsPanel /></TabsContent>
          <TabsContent value="media"><MediaPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
